// 设置 → 对外接入：个人 Key 范围、可选知识库权限、实时工具目录与管理员元数据审计。
// 分区是知识库的顶层文件夹（studio 现读）；哪些默认开放由 studio 定（派生整理（未经人审）、收集区这些默认不开），网页只照着显示。

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App as AntApp, Modal } from 'antd'
import { Copy, KeyRound, Plus } from 'lucide-react'
import { getMe } from '../../api/settings'
import { createOpenKey, deleteOpenKey, disableOpen, enableOpen, getOpen, listOpenCalls, mcpUrl, renameOpen, revokeOpenKey, TOOL_LABEL, updateOpenKey, type NewOpenKey, type OpenKey, type OpenSection } from '../../api/open'
import { Pill } from '../../ui'
import { useKnowledgeBases } from '../chat/Composer'
import { connectGuides } from './connect'
import { PlatformTools } from './PlatformTools'
import type { OpenScope } from '../../api/hub/open'
import './personal-access.css'

const SCOPE_LABELS: Record<OpenScope, string> = { 'data:read': '读数据', 'home:write': '改我的个人主页', 'app:publish': '发布仅自己可见的应用', 'tasks:write': '提交和更新任务', 'decisions:submit': '提交待决定', 'approvals:start': '发起审批（不能批准）', 'posts:write': '发灵感墙帖子', 'views:write': '保存和管理私人视图' }
const scopesOf = (key: OpenKey | null): OpenScope[] => key?.scopes ?? ['data:read']

const when = (iso?: string) => (iso ? new Intl.DateTimeFormat('zh-CN', { timeZone: 'Asia/Shanghai', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) : '—')
/** 没改过称呼时：公司知识库，不用个人名字。 */
const DEFAULT_OWNER = '灵智原生'
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((x) => b.includes(x))

async function copyText(text: string, message: { success: (s: string) => void; warning: (s: string) => void }) {
  try {
    await navigator.clipboard.writeText(text)
    message.success('复制好了')
  } catch {
    message.warning('没能自动复制，请手动选中复制')
  }
}

export function OpenAccess() {
  const qc = useQueryClient()
  const { message, modal } = AntApp.useApp()
  const me = useQuery({ queryKey: ['me'], queryFn: getMe })
  const tenant = me.data?.tenant?.id
  const kbs = (useKnowledgeBases().data ?? []).filter((k) => k.type !== 'faq')
  const open = useQuery({ queryKey: ['open'], queryFn: getOpen })
  const [owner, setOwner] = useState<string | null>(null)
  const [editing, setEditing] = useState<OpenKey | 'new' | null>(null)
  const [made, setMade] = useState<{ key: OpenKey; token: string } | null>(null)
  const [logOf, setLogOf] = useState<OpenKey | null>(null)
  const refresh = () => qc.invalidateQueries({ queryKey: ['open'] })

  const enable = useMutation({
    mutationFn: () => enableOpen(tenant!, kbs.map((k) => k.id), (owner ?? DEFAULT_OWNER).trim()),
    onSuccess: () => {
      void refresh()
      qc.invalidateQueries({ queryKey: ['api-keys'] })
      message.success('开通了')
    },
    onError: (e) => void message.error(e.message),
  })
  const disable = useMutation({
    mutationFn: () => disableOpen(tenant!),
    onSuccess: () => {
      void refresh()
      qc.invalidateQueries({ queryKey: ['api-keys'] })
      message.success('停用了')
    },
    onError: (e) => void message.error(e.message),
  })
  const [renaming, setRenaming] = useState<string | null>(null)
  const rename = useMutation({
    mutationFn: (name: string) => renameOpen(name),
    onSuccess: () => {
      setRenaming(null)
      void refresh()
    },
    onError: (e) => void message.error(e.message),
  })
  const revoke = useMutation({
    mutationFn: (id: string) => revokeOpenKey(id),
    onSuccess: () => {
      void refresh()
      message.success('吊销了')
    },
    onError: (e) => void message.error(e.message),
  })

  const purge = useMutation({
    mutationFn: (id: string) => deleteOpenKey(id),
    onSuccess: () => void refresh(),
    onError: (e) => void message.error(e.message),
  })

  const s = open.data
  // 只开放内部空间：网页登录的不是那个空间时，建出来的 Key 是别的空间的，开通不了
  const away = s !== undefined && tenant !== undefined && String(tenant) !== s.tenant
  const knowledgeEnabled = s?.knowledgeEnabled ?? Boolean(s?.enabled && s?.sections?.length)
  const sections = s?.sections ?? []
  const defaults = sections.filter((x) => x.defaultOpen).map((x) => x.name)
  const docs = sections.reduce((n, x) => n + x.count, 0)
  const live = (s?.keys ?? []).filter((k) => !k.revokedAt)
  const gone = (s?.keys ?? []).filter((k) => k.revokedAt)

  return (
    <>
      <section className="settings-card" aria-label="对外接入">
        <div className="settings-h">
          <h2>对外接入（MCP）</h2>
          <span className="grow" />
          {s?.enabled && (
            <Pill
              size="sm"
              className="danger"
              disabled={tenant === undefined || disable.isPending}
              onClick={() =>
                modal.confirm({
                  title: '停用对外接入？',
                  content: '发出去的 Key 会马上全部失效（调用记录留着）。以后再开通，要重新发 Key。',
                  okText: '停用',
                  cancelText: '不了',
                  okButtonProps: { danger: true },
                  onOk: () => disable.mutateAsync(),
                })
              }
            >
              停用
            </Pill>
          )}
        </div>
        <p className="field-note">用一把个人 Key，让你的 AI 读中台数据、提交任务和待决定。代理始终以「你的 AI」出现，权限不超过你本人。新 Key 默认只读数据，其他能力由你勾选，随时可撤销。共享给全员必须本人在网页操作，敏感数据和外部域名由 CEO 审批。知识库可选，同一个 MCP 地址连接。</p>
        {open.isError && <p className="field-note">没能载入：{open.error.message}</p>}
        {s && !knowledgeEnabled && (
          <form
            className="dlg"
            onSubmit={(e) => {
              e.preventDefault()
              if (tenant !== undefined && !away && kbs.length) enable.mutate()
            }}
          >
            <label htmlFor="open-owner">别人的 AI 怎么称呼知识库的主人</label>
            <input id="open-owner" value={owner ?? DEFAULT_OWNER} maxLength={40} onChange={(e) => setOwner(e.target.value)} />
            <p className="field-note">写进给 AI 的说明和研究报告里（「这是……的内部知识库」），不用个人名字；以后能改。</p>
            {away ? (
              <p className="field-note" data-bad="true">
                对外接入只开放内部空间（{s.tenant}）的资料，你现在登录的是「{me.data?.tenant?.name ?? tenant}」，开通不了。
              </p>
            ) : (
              <p className="field-note">开通时会在内部空间的「API Key」里建一把只能检索的 Key，只给本服务用、不发给任何人；别人能查的只有{kbs.map((k) => `「${k.name}」`).join('') || '内部知识库'}里你开放的分区。</p>
            )}
            <div className="dlg-acts">
              <Pill type="submit" variant="primary" disabled={tenant === undefined || away || !kbs.length || enable.isPending || !(owner ?? DEFAULT_OWNER).trim()}>
                {enable.isPending ? '开通中…' : '开通'}
              </Pill>
            </div>
          </form>
        )}
        {s && (
          <>
            {s.error && (
              <p className="field-note" data-bad="true">
                本服务现在查不了内部知识库：{s.error}。请检查知识库只读 Key；个人数据 Key 的范围独立设置。
              </p>
            )}
            <dl className="settings-dl">
              <dt>MCP 地址</dt>
              <dd className="open-url">
                <code>{mcpUrl()}</code>
                <Pill size="sm" icon={<Copy />} aria-label="复制 MCP 地址" onClick={() => void copyText(mcpUrl(), message)} />
              </dd>
              {knowledgeEnabled && <><dt>能开放的</dt>
              <dd>
                {sections.length} 个分区、{docs} 篇
              </dd>
              <dt>称呼主人</dt>
              <dd className="open-url">
                {renaming === null ? (
                  <>
                    {s.ownerName}
                    <Pill size="sm" onClick={() => setRenaming(s.ownerName ?? '')}>
                      改
                    </Pill>
                  </>
                ) : (
                  <form
                    className="open-rename"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (renaming.trim()) rename.mutate(renaming.trim())
                    }}
                  >
                    <input aria-label="别人的 AI 怎么称呼知识库的主人" value={renaming} maxLength={40} onChange={(e) => setRenaming(e.target.value)} autoFocus />
                    <Pill size="sm" type="submit" variant="primary" disabled={!renaming.trim() || rename.isPending}>
                      保存
                    </Pill>
                    <Pill size="sm" onClick={() => setRenaming(null)}>
                      取消
                    </Pill>
                  </form>
                )}
              </dd>
              </>}
            </dl>
          </>
        )}
      </section>

      {s && !away && (
        <section className="settings-card" aria-label="发出去的 Key">
          <div className="settings-h">
            <h2>个人 Key</h2>
            <span className="grow" />
            <Pill size="sm" icon={<Plus />} onClick={() => setEditing('new')}>
              新建 Key
            </Pill>
          </div>
          <p className="field-note">完整的 Key 只在新建时显示一次，调用记录不显示 Key 或请求正文。每个客户端分别建 Key；开工先读「我的变化摘要」和「公司规矩」。</p>
          {!live.length && <p className="field-note">还没有发过 Key。</p>}
          <ul className="key-list">
            {[...live, ...gone].map((k) => (
              <li key={k.id} aria-label={k.name} data-gone={k.revokedAt ? 'true' : undefined}>
                <KeyRound width={16} height={16} />
                <div className="key-main">
                  <b>{k.name}</b>
                  <span className="field-note">{k.agentName ?? `${me.data?.user?.username || "本人"}的 AI`} · 客户端：{k.clientLabel ?? k.name}</span>
                  <code>{k.prefix}…</code>
                  <span className="key-caps">
                    {scopesOf(k).map(scope => <span key={scope} className="sk-chip">{SCOPE_LABELS[scope]}</span>)}
                    {defaults.length > 0 && sameSet(k.sections, defaults) ? (
                      <span className="sk-chip">默认的 {defaults.length} 个分区</span>
                    ) : (
                      k.sections.map((x) => (
                        <span key={x} className="sk-chip">
                          {x}
                        </span>
                      ))
                    )}
                  </span>
                  {!k.sections.length && <span className="field-note">只用于中台数据，未开放知识库</span>}
                  <span className="field-note">
                    全文：{!k.fullText.length ? '都不给' : sameSet(k.fullText, k.sections) ? '能查的都给' : k.fullText.join('、')}（其余只给摘要、片段和原文链接）· {k.research ? '能深入研究' : '不能深入研究'} · 每小时最多 {k.perHour} 次
                  </span>
                  <span className="field-note">
                    {k.revokedAt ? `${when(k.revokedAt)} 吊销` : `最近 24 小时 ${k.calls24h} 次 · 最近用过：${when(k.lastUsedAt)}`} · 建于 {when(k.createdAt)}
                    {k.expiresAt && !k.revokedAt ? ` · ${when(k.expiresAt)} 过期` : ''}
                  </span>
                </div>
                <div className="open-acts">
                  <Pill size="sm" onClick={() => setLogOf(k)}>
                    记录
                  </Pill>
                  {k.revokedAt && (
                    <Pill
                      size="sm"
                      onClick={() =>
                        modal.confirm({
                          title: `删掉「${k.name}」？`,
                          content: '连同它的调用记录一起删掉，删了就找不回来。',
                          okText: '删掉',
                          cancelText: '不了',
                          okButtonProps: { danger: true },
                          onOk: () => purge.mutateAsync(k.id),
                        })
                      }
                    >
                      删掉
                    </Pill>
                  )}
                  {!k.revokedAt && (
                    <>
                      <Pill size="sm" onClick={() => setEditing(k)}>
                        设置
                      </Pill>
                      <Pill
                        size="sm"
                        className="danger"
                        onClick={() =>
                          modal.confirm({
                            title: `吊销「${k.name}」？`,
                            content: '用这把 Key 的 AI 会马上连不上。',
                            okText: '吊销',
                            cancelText: '不了',
                            okButtonProps: { danger: true },
                            onOk: () => revoke.mutateAsync(k.id),
                          })
                        }
                      >
                        吊销
                      </Pill>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <PlatformTools />

      {editing && (
        <KeyDialog
          sections={sections}
          knowledgeEnabled={knowledgeEnabled}
          k={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={(r) => {
            setEditing(null)
            void refresh()
            if (r) setMade(r)
          }}
        />
      )}
      {made && <MadeDialog made={made} onClose={() => setMade(null)} />}
      {logOf && <CallsDialog k={logOf} onClose={() => setLogOf(null)} />}
    </>
  )
}

export function KeyDialog({ sections, knowledgeEnabled, k, onClose, onDone }: { sections: OpenSection[]; knowledgeEnabled: boolean; k: OpenKey | null; onClose: () => void; onDone: (made?: { key: OpenKey; token: string }) => void }) {
  const { message } = AntApp.useApp()
  const [name, setName] = useState(k?.name ?? '')
  // 新 Key 默认：默认开放的分区都能查、都给全文（公司文档，给同事用）
  const opened = sections.filter((s) => s.defaultOpen).map((s) => s.name)
  const [picked, setPicked] = useState<string[]>(k?.sections ?? opened)
  const [full, setFull] = useState<string[]>(k?.fullText ?? opened)
  const [research, setResearch] = useState(k?.research ?? knowledgeEnabled)
  const [scopes, setScopes] = useState<OpenScope[]>(scopesOf(k))
  const [perHour, setPerHour] = useState(String(k?.perHour ?? 60))
  const [days, setDays] = useState('90')
  const save = useMutation({
    mutationFn: async () => {
      const body: NewOpenKey = { name: name.trim(), sections: picked, fullText: full.filter((f) => picked.includes(f)), research: knowledgeEnabled && picked.length > 0 && research, scopes, perHour: Number(perHour) }
      if (k) {
        await updateOpenKey(k.id, body)
        return undefined
      }
      return createOpenKey({ ...body, expiresInDays: Number(days) })
    },
    onSuccess: (r) => onDone(r),
    onError: (e) => void message.error(e.message),
  })
  const toggle = (list: string[], set: (l: string[]) => void, n: string) => set(list.includes(n) ? list.filter((x) => x !== n) : [...list, n])
  const hour = Number(perHour)
  const ok = name.trim() && Number.isInteger(hour) && hour >= 1 && hour <= 1000
  return (
    <Modal open title={k ? `「${k.name}」的设置` : '新建 Key'} onCancel={onClose} footer={null} destroyOnHidden width={560}>
      <form
        className="dlg"
        onSubmit={(e) => {
          e.preventDefault()
          if (ok) save.mutate()
        }}
      >
        <label htmlFor="open-key-name">给谁用</label>
        <input id="open-key-name" value={name} maxLength={60} placeholder="比如：小王的 Claude Code" onChange={(e) => setName(e.target.value)} autoFocus />
        <fieldset className="personal-access-scopes">
          <legend>这把 Key 能做什么</legend>
          {(Object.entries(SCOPE_LABELS) as [OpenScope, string][]).map(([scope, label]) => <label key={scope}>
            <input type="checkbox" checked={scopes.includes(scope)} onChange={() => setScopes(scopes.includes(scope) ? scopes.filter(x => x !== scope) : [...scopes, scope])} /> {label}
          </label>)}
          <p className="field-note">四个业务写范围默认关闭，旧 Key 不会自动升级。保存取消勾选后立即生效；其他 Key 不受影响。改主页和应用仅作用于你本人。</p>
        </fieldset>
        {!knowledgeEnabled && <p className="field-note">知识库尚未开通，这把 Key 可用于中台数据。以后可以另行开通知识库。</p>}
        {knowledgeEnabled && <><label>能查哪些分区</label>
        <div className="open-sections" role="group" aria-label="能查哪些分区">
          {sections.map((s) => (
            <label key={s.name} className="open-section">
              <input type="checkbox" checked={picked.includes(s.name)} onChange={() => toggle(picked, setPicked, s.name)} />
              <span>
                <b>{s.name}</b> <small>{s.count} 篇{s.defaultOpen ? '' : ' · 默认不开'}</small>
                <small className="open-about">{s.about}</small>
              </span>
            </label>
          ))}
        </div>
        {!picked.length && <p className="field-note">只用于中台数据，未开放知识库</p>}
        <label>哪些给全文</label>
        <p className="field-note">没勾的只给摘要、相关的几段和原文链接。不管给不给全文，每篇都带审核标记（未审核、派生整理，未经人审）和飞书原文链接。</p>
        <div className="kb-checks" role="group" aria-label="哪些给全文">
          {sections
            .filter((s) => picked.includes(s.name))
            .map((s) => (
              <label key={s.name} className="kb-check">
                <input type="checkbox" checked={full.includes(s.name)} onChange={() => toggle(full, setFull, s.name)} />
                {s.name}
              </label>
            ))}
        </div>
        <label className="kb-check open-research">
          <input type="checkbox" checked={picked.length > 0 && research} disabled={!picked.length} onChange={(e) => setResearch(e.target.checked)} />
          能用深入研究（要调模型、花额度，20–40 秒一次；每小时最多 5 次）
        </label>
        </>}
        <label htmlFor="open-key-hour">每小时最多调几次</label>
        <input id="open-key-hour" inputMode="numeric" value={perHour} onChange={(e) => setPerHour(e.target.value.replace(/\D/g, ''))} />
        {!k && (
          <>
            <label htmlFor="open-key-days">有效期</label>
            <select id="open-key-days" value={days} onChange={(e) => setDays(e.target.value)}>
              <option value="30">30 天</option>
              <option value="90">90 天</option>
              <option value="365">一年</option>
              <option value="0">不过期</option>
            </select>
          </>
        )}
        <div className="dlg-acts">
          <Pill type="submit" variant="primary" disabled={!ok || save.isPending}>
            {k ? '保存' : '新建'}
          </Pill>
          <Pill onClick={onClose}>取消</Pill>
        </div>
      </form>
    </Modal>
  )
}

function MadeDialog({ made, onClose }: { made: { key: OpenKey; token: string }; onClose: () => void }) {
  const { message } = AntApp.useApp()
  const guides = connectGuides(mcpUrl(), made.token)
  const [tab, setTab] = useState(guides[0].id)
  const g = guides.find((x) => x.id === tab) ?? guides[0]
  return (
    <Modal open title={`「${made.key.name}」建好了`} onCancel={onClose} footer={null} destroyOnHidden maskClosable={false} width={640}>
      <div className="dlg open-made">
        <p className="field-note" data-bad="true">
          完整的 Key 只显示这一次，关掉以后就看不到了。把下面的接法连同 Key 一起发给对方。
        </p>
        <code className="key-token" aria-label="新的 Key">
          {made.token}
        </code>
        <div className="open-tabs" role="tablist" aria-label="怎么接">
          {guides.map((x) => (
            <button key={x.id} type="button" role="tab" aria-selected={x.id === tab} onClick={() => setTab(x.id)}>
              {x.name}
            </button>
          ))}
        </div>
        <div role="tabpanel" aria-label={g.name} className="open-guide">
          <p className="field-note">{g.how}</p>
          <pre>
            <code>{g.snippet}</code>
          </pre>
          <Pill size="sm" icon={<Copy />} onClick={() => void copyText(g.snippet, message)}>
            复制
          </Pill>
        </div>
        <div className="dlg-acts">
          <Pill variant="primary" icon={<Copy />} onClick={() => void copyText(made.token, message)}>
            只复制 Key
          </Pill>
          <Pill onClick={onClose}>我已经存好了</Pill>
        </div>
      </div>
    </Modal>
  )
}

function CallsDialog({ k, onClose }: { k: OpenKey; onClose: () => void }) {
  const calls = useQuery({ queryKey: ['open-calls', k.id], queryFn: () => listOpenCalls(k.id) })
  return (
    <Modal open title={`「${k.name}」的调用记录`} onCancel={onClose} footer={null} destroyOnHidden width={720}>
      {calls.isError && <p className="field-note">没能载入：{calls.error.message}</p>}
      {calls.isSuccess && !calls.data.length && <p className="field-note">还没有用过。</p>}
      {!!calls.data?.length && (
        <table className="settings-table">
          <thead>
            <tr>
              <th>时间</th>
              <th>做什么</th>
              <th>结果</th>
              <th>给了</th>
              <th>用时</th>
            </tr>
          </thead>
          <tbody>
            {calls.data.map((c, i) => (
              <tr key={i}>
                <td>{when(c.at)}</td>
                <td>{TOOL_LABEL[c.tool] ?? c.tool}</td>
                <td>
                  {c.ok ? '成功' : '失败'}
                </td>
                <td>{c.ok ? `${c.count} 篇` : '—'}</td>
                <td>{(c.ms / 1000).toFixed(1)} 秒</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  )
}
